/**
 * Canvas & Surface Types
 *
 * Types for the AUI canvas system. Agents create canvases within
 * conversations and render declarative UI surfaces into them.
 */

/** Layout hint for how surfaces within a canvas should be arranged */
export type CanvasLayout = 'tabs' | 'grid' | 'stack';

/** A persistent container for UI surfaces within a conversation */
export interface Canvas {
  canvasId: string;
  conversationId: string;
  title: string;
  isDefault: boolean;
  layout: CanvasLayout;
  order?: number;
  createdAt: string;
}

/** An interactive action declared on an AUI component */
export interface AuiAction {
  name: string;
  label: string;
  payload?: Record<string, unknown>;
}

/** A single node in the flat AUI component tree */
export interface AuiNode {
  id: string;
  component: {
    type: string;
    props?: Record<string, unknown>;
    children?: string[];
    actions?: AuiAction[];
  };
}

/** A declarative UI panel within a canvas */
export interface Surface {
  surfaceId: string;
  /**
   * Agent-provided surface name, unique within the canvas. Distinct from the
   * platform `surfaceId` (`srf-…`) and from the human-readable `title`. This is
   * the identifier the backend's `/dispatch-action` endpoint requires when an
   * AUI action button is clicked — see `useDispatchAction`.
   */
  surfaceName?: string;
  canvasId: string;
  runId?: string;
  title: string;
  version: number;
  spec: { updates: AuiNode[] };
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Frontend-owned UI state (filters, sort order, selected tab, etc.).
   * Opaque to the agent: never injected into prompts, never set or read by
   * tools. Preserved server-side across agent-driven update_surface calls.
   * Convention: keyed by AuiNode.id so each component owns its own slice.
   */
  uiState?: Record<string, unknown>;
}

/** Sort comparator for canvases. Default canvas pinned first; then `order` ascending; then createdAt ascending. */
export function compareCanvases(a: Canvas, b: Canvas): number {
  if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
  const ao = a.order ?? Infinity;
  const bo = b.order ?? Infinity;
  if (ao !== bo) return ao - bo;
  if (a.createdAt === b.createdAt) return 0;
  return a.createdAt < b.createdAt ? -1 : 1;
}

function hasTaskDashboardNode(s: Surface): boolean {
  return s.spec?.updates?.some((n) => n.component.type === 'TaskDashboard') ?? false;
}

function effectiveSurfaceOrder(s: Surface): number {
  if (s.order !== undefined) return s.order;
  // Auto-pin the operator's task-dashboard surface above unordered surfaces.
  // 0 wins against drag-reorder values, which start at 1 (CanvasContext surfaces_reordered).
  if (hasTaskDashboardNode(s)) return 0;
  return Infinity;
}

/** Sort comparator for surfaces. `order` ascending (TaskDashboard auto-pinned when unordered); then createdAt descending. */
export function compareSurfaces(a: Surface, b: Surface): number {
  const ao = effectiveSurfaceOrder(a);
  const bo = effectiveSurfaceOrder(b);
  if (ao !== bo) return ao - bo;
  const ac = a.createdAt ?? '';
  const bc = b.createdAt ?? '';
  if (ac === bc) return 0;
  return ac < bc ? 1 : -1;
}

/** WebSocket event data for canvas_created */
export interface CanvasCreatedEventData {
  runId: string;
  agentId: string;
  canvasId: string;
  conversationId?: string;
  title: string;
  isDefault: boolean;
  layout: CanvasLayout;
  iteration: number;
}

/** WebSocket event data for surface_update */
export interface SurfaceUpdateEventData {
  runId: string;
  agentId: string;
  canvasId: string;
  surfaceId: string;
  /** Agent-provided surface name (unique within canvas); see Surface.surfaceName. */
  surfaceName?: string;
  conversationId?: string;
  title: string;
  version: number;
  spec: { updates: AuiNode[] };
  iteration: number;
  createdAt?: string;
  updatedAt?: string;
  /** Frontend-owned. See Surface.uiState. */
  uiState?: Record<string, unknown>;
}

/** WebSocket event data for surface_deleted */
export interface SurfaceDeletedEventData {
  runId: string;
  canvasId: string;
  surfaceId: string;
  conversationId?: string;
  success: boolean;
  error?: string;
  durationMs?: number;
  iteration: number;
}

/** Request body for POST /api/v1/canvases/{canvasId}/surfaces/move */
export interface MoveSurfacesInput {
  surfaceIds: string[];
  targetCanvasId?: string;
  newCanvasTitle?: string;
  newCanvasLayout?: CanvasLayout;
  targetConversationId?: string;
}

/** Response from POST /api/v1/canvases/{canvasId}/surfaces/move */
export interface MoveSurfacesResponse {
  fromCanvasId: string;
  toCanvasId: string;
  fromConversationId: string;
  toConversationId: string;
  canvasCreated?: true;
  toCanvasTitle?: string;
  toCanvasLayout?: CanvasLayout;
  surfaces: Array<{ surfaceId: string; surfaceName: string; version: number }>;
}

/** WebSocket event data for agent.run.surface_moved */
export interface SurfaceMovedEventData {
  runId?: string;
  iteration: number;
  surfaceId: string;
  surfaceName: string;
  fromCanvasId: string;
  toCanvasId: string;
  fromConversationId: string;
  toConversationId: string;
  version: number;
  success: boolean;
  error?: string;
  durationMs?: number;
}

/** WebSocket event data for entity.canvas.updated — additive metadata changes (incl. order). */
export interface CanvasEntityUpdatedEventData {
  canvasId: string;
  conversationId: string;
  title?: string;
  layout?: CanvasLayout;
  order?: number;
}

/** WebSocket event data for entity.surface.updated — additive metadata changes (incl. order). */
export interface SurfaceEntityUpdatedEventData {
  surfaceId: string;
  surfaceName: string;
  canvasId: string;
  conversationId: string;
  version: number;
  title?: string;
  order?: number;
}
